import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlatformRole } from '../../../api/platformRoles.api';
import { ALL_PLATFORM_PERMISSIONS, PLATFORM_PERMISSION_GROUPS } from '../../../config/platform-permissions';

export const COLORS = ['teal', 'emerald', 'amber', 'rose', 'orange', 'green', 'pink', 'sky'] as const;
export type RoleColor = (typeof COLORS)[number];

export const colorCfg: Record<RoleColor, { bg: string; light: string; text: string; dot: string }> = {
  teal: { bg: 'bg-teal-600', light: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
  emerald: { bg: 'bg-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  amber: { bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  rose: { bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-600', dot: 'bg-rose-500' },
  orange: { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  green: { bg: 'bg-green-600', light: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  pink: { bg: 'bg-pink-500', light: 'bg-pink-50', text: 'text-pink-600', dot: 'bg-pink-500' },
  sky: { bg: 'bg-sky-600', light: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-500' },
};

/** Safe lookup — unknown DB colors must not crash the page. */
export function resolveRoleColor(color?: string | null): RoleColor {
  if (color && (COLORS as readonly string[]).includes(color)) {
    return color as RoleColor;
  }
  return 'teal';
}

interface RoleFormModalProps {
  role: PlatformRole | null;
  isOpen: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string; color: string; permissions: string[] }) => void | Promise<void>;
}

export default function RoleFormModal({ role, isOpen, saving, onClose, onSave }: RoleFormModalProps) {
  const { t } = useTranslation();
  const isEdit = !!role;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<RoleColor>('teal');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (role) {
      setName(role.name);
      setDescription(role.description ?? '');
      setColor(resolveRoleColor(role.color));
      setSelected(new Set(role.permissions ?? []));
    } else {
      setName('');
      setDescription('');
      setColor('teal');
      setSelected(new Set());
    }
    setNameError('');
  }, [role, isOpen]);

  if (!isOpen) return null;

  const togglePerm = (perm: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
  };

  const toggleGroup = (perms: string[]) => {
    const allOn = perms.every((p) => selected.has(p));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOn) perms.forEach((p) => next.delete(p));
      else perms.forEach((p) => next.add(p));
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === ALL_PLATFORM_PERMISSIONS.length) setSelected(new Set());
    else setSelected(new Set(ALL_PLATFORM_PERMISSIONS));
  };

  const handleSave = () => {
    if (!name.trim()) {
      setNameError('Role name is required');
      return;
    }
    void onSave({
      name: name.trim(),
      description: description.trim(),
      color,
      permissions: [...selected],
    });
  };

  const cc = colorCfg[color];
  const isAllSelected = selected.size === ALL_PLATFORM_PERMISSIONS.length;
  const lockSystemName = role?.is_system === true;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl my-6 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${cc.light}`}>
              <i className={`ri-shield-user-line ${cc.text}`} />
            </div>
            <h3 className="text-base font-bold text-gray-900">
              {isEdit ? t('admin_roles.form.title_edit', { defaultValue: 'تعديل الدور' }) : t('admin_roles.form.title_add', { defaultValue: 'دور جديد' })}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">اسم الدور</label>
              <input
                value={name}
                disabled={lockSystemName}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg disabled:bg-gray-50"
              />
              {nameError ? <p className="text-xs text-rose-500 mt-1">{nameError}</p> : null}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">الوصف</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">اللون</label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full ${colorCfg[c].bg} ${color === c ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-gray-800">
              الصلاحيات ({selected.size}/{ALL_PLATFORM_PERMISSIONS.length})
            </p>
            <button type="button" onClick={toggleAll} className="text-xs font-semibold text-teal-600">
              {isAllSelected ? t('admin_roles.deselect_all') : t('admin_roles.select_all')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {PLATFORM_PERMISSION_GROUPS.map((group) => {
              const allOn = group.perms.every((p) => selected.has(p));
              const noneOn = group.perms.every((p) => !selected.has(p));
              return (
                <div key={group.key} className="rounded-xl border border-gray-100 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <i className={`${group.icon} text-teal-600`} />
                      <p className="text-sm font-bold">{t(`admin_roles.groups.${group.key}`, { defaultValue: group.key })}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleGroup([...group.perms])}
                      className="text-[11px] font-semibold text-teal-600"
                    >
                      {allOn ? 'إلغاء الكل' : noneOn ? 'تفعيل الموديول' : 'إكمال'}
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {group.perms.map((perm) => (
                      <label key={perm} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={selected.has(perm)} onChange={() => togglePerm(perm)} />
                        {t(`admin_roles.perms.${perm}`, { defaultValue: perm })}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium">
            إلغاء
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="flex-1 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
          >
            {saving ? '…' : 'حفظ الدور'}
          </button>
        </div>
      </div>
    </div>
  );
}
